// The live connection: one WebSocket for everything the engine publishes
// (snapshots each second, decisions, alerts, orders, fills, bars, quotes).
import { create } from "zustand";
import type { Alert, Bar, Decision, Mode, Quote, Snapshot } from "./types";

type Handler = (payload: any) => void;

interface LiveState {
  status: "connecting" | "open" | "closed";
  snapshot: Snapshot | null;
  snapshotAt: number;
  decisions: Decision[];
  alerts: Alert[];
  quotes: Record<string, Quote>;
  forming: Record<string, Record<string, Bar>>;
  view: Mode;
  setView: (m: Mode) => void;
}

function storedView(): Mode {
  try {
    return localStorage.getItem("at.view") === "LIVE" ? "LIVE" : "PAPER";
  } catch {
    return "PAPER";
  }
}

export const useLive = create<LiveState>((set) => ({
  status: "connecting",
  snapshot: null,
  snapshotAt: 0,
  decisions: [],
  alerts: [],
  quotes: {},
  forming: {},
  view: storedView(),
  setView: (view) => {
    try {
      localStorage.setItem("at.view", view);
    } catch {
      /* private mode */
    }
    set({ view });
  },
}));

const handlers = new Map<string, Set<Handler>>();
export function subscribe(topic: string, fn: Handler): () => void {
  if (!handlers.has(topic)) handlers.set(topic, new Set());
  handlers.get(topic)!.add(fn);
  return () => handlers.get(topic)?.delete(fn);
}

let ws: WebSocket | null = null;
let backoff = 1000;
let stopped = true;
let pingTimer: number | undefined;
let watchTimer: number | undefined;
const watches = new Map<string, { keys: string[]; tfs: string[] }>();

function send(msg: unknown) {
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
}

function pushWatches() {
  const keys = new Set<string>();
  const tfs = new Set<string>();
  for (const w of watches.values()) {
    w.keys.forEach((k) => keys.add(k));
    w.tfs.forEach((t) => tfs.add(t));
  }
  if (keys.size) send({ type: "watch", keys: [...keys].slice(0, 60), tfs: [...tfs] });
}

let watchSeq = 0;

/** Ask the engine to stream these instruments while the caller is mounted. */
export function watch(id: string, keys: string[], tfs: string[] = []): () => void {
  // each call gets its own entry: two charts of the same symbol share an id, and
  // one unmounting must not stop the other's stream
  const token = `${id}#${++watchSeq}`;
  watches.set(token, { keys, tfs });
  pushWatches();
  return () => {
    watches.delete(token);
  };
}

function dispatch(topic: string, payload: any) {
  const st = useLive.getState();
  switch (topic) {
    case "snapshot":
      useLive.setState({ snapshot: payload as Snapshot, snapshotAt: Date.now() });
      break;
    case "decision":
      if (payload.kind !== "heartbeat")
        useLive.setState({ decisions: [payload as Decision, ...st.decisions].slice(0, 400) });
      break;
    case "alert":
      useLive.setState({ alerts: [payload as Alert, ...st.alerts].slice(0, 200) });
      break;
    case "live": {
      const quotes = { ...st.quotes, ...(payload.quotes ?? {}) };
      const forming = { ...st.forming };
      for (const [k, v] of Object.entries(payload.forming ?? {})) forming[k] = { ...(forming[k] ?? {}), ...(v as object) };
      useLive.setState({ quotes, forming });
      break;
    }
  }
  handlers.get(topic)?.forEach((fn) => {
    try {
      fn(payload);
    } catch (e) {
      console.error("live handler failed", topic, e);
    }
  });
}

function connect() {
  // a reconnect timer from before a restart finds a socket already open or opening
  if (stopped || (ws && ws.readyState <= WebSocket.OPEN)) return;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  useLive.setState({ status: "connecting" });
  const sock = new WebSocket(`${proto}://${location.host}/api/ws`);
  ws = sock;
  sock.onopen = () => {
    backoff = 1000;
    useLive.setState({ status: "open" });
    pushWatches();
  };
  sock.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      dispatch(msg.topic, msg.payload);
    } catch {
      /* ignore malformed */
    }
  };
  sock.onclose = (ev) => {
    // a socket already replaced (stopLive and startLive in quick succession) must
    // neither report nor reconnect: that left two sockets, every event twice
    if (ws !== sock) return;
    ws = null;
    useLive.setState({ status: "closed" });
    if (ev.code === 4401) return; // not signed in: the app will show the login page
    if (!stopped) window.setTimeout(connect, backoff);
    backoff = Math.min(backoff * 2, 15000);
  };
  sock.onerror = () => sock.close();
}

/** Back in front (a phone unlocked, the app switched to) or back online: a phone
 * suspends a background page and its socket dies, often without a close event.
 * Reconnect at once instead of after the backoff, and drop a socket that has
 * gone quiet (the engine sends a snapshot every second). */
function revive() {
  if (stopped || document.visibilityState !== "visible") return;
  const quiet = Date.now() - useLive.getState().snapshotAt > STALE_MS;
  if (ws && ws.readyState === WebSocket.OPEN && quiet && useLive.getState().snapshotAt > 0) {
    ws.close(); // onclose reconnects
    return;
  }
  if (!ws || ws.readyState > WebSocket.OPEN) {
    backoff = 1000;
    connect();
  }
}
const STALE_MS = 8000;
let staleTimer: number | undefined;

export function startLive() {
  if (!stopped) return;
  stopped = false;
  connect();
  pingTimer = window.setInterval(() => send({ type: "ping" }), 20000);
  // watches expire on the engine after 90 s unless renewed
  watchTimer = window.setInterval(pushWatches, 45000);
  staleTimer = window.setInterval(revive, 4000);
  document.addEventListener("visibilitychange", revive);
  window.addEventListener("online", revive);
  window.addEventListener("pageshow", revive);
}

export function stopLive() {
  stopped = true;
  window.clearInterval(pingTimer);
  window.clearInterval(watchTimer);
  window.clearInterval(staleTimer);
  document.removeEventListener("visibilitychange", revive);
  window.removeEventListener("online", revive);
  window.removeEventListener("pageshow", revive);
  const sock = ws;
  ws = null;
  sock?.close();
  useLive.setState({ status: "closed" });
}
